import { cx } from '@emotion/css';
import { useMemoizedFn } from 'ahooks';
import { useRef } from 'react';

interface SplitLineProps {
  /**
   * 分割线类型 水平或垂直分割线
   * @default 'horizontal' 水平分割线
   */
  type: 'horizontal' | 'vertical';
  max: number;
  min: number;
  target: React.RefObject<HTMLDivElement>;
  position: 'left' | 'right' | 'top' | 'bottom';
  onDone?: (newValue: number, type: 'width' | 'height') => void;
}

type SplitLineInstanceProps = Omit<SplitLineProps, 'type'>;

/**
 * 水平分割线
 */
const HorizontalSplitLine = (props: SplitLineInstanceProps) => {
  const { target, max, min, position, onDone } = props;
  const valueRef = useRef(0);
  const onSplitMouseDown = useMemoizedFn((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const startHeight = event.clientY;
    const paneHeight = target.current?.getBoundingClientRect().height ?? 0;
    event.preventDefault();

    const mousemove = (e: MouseEvent) => {
      requestAnimationFrame(() => {
        const calH = startHeight - e.clientY;
        valueRef.current = paneHeight + calH;
        if (valueRef.current > max) {
          document.body.style.cursor = 's-resize';
        } else if (valueRef.current < min) {
          document.body.style.cursor = 'n-resize';
        } else {
          document.body.style.cursor = 'row-resize';
          if (target.current) {
            target.current.style.height = `${valueRef.current}px`;
          }
        }
      });
    };
    const mouseup = () => {
      requestAnimationFrame(() => {
        document.body.style.cursor = '';
        onDone?.(Math.max(Math.min(valueRef.current, max), min), 'height');
      });
      document.removeEventListener('mousemove', mousemove);
      document.removeEventListener('mouseup', mouseup);
    };
    document.addEventListener('mouseup', mouseup);
    document.addEventListener('mousemove', mousemove);
  });

  return (
    <div
      className={cx(
        'absolute w-full h-1.5 right-0 left-0 cursor-row-resize z-[2]',
        position === 'top' && '-top-1.5',
        position === 'bottom' && '-bottom-1.5',
      )}
      onMouseDown={onSplitMouseDown}
    />
  );
};

/**
 * 垂直分割线
 */
const VerticalSplitLine = (props: SplitLineInstanceProps) => {
  const { target, max, min, position, onDone } = props;
  const valueRef = useRef(0);

  const onSplitMouseDown = useMemoizedFn((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const startWidth = event.clientX;
    const wrapperWidth = target.current?.getBoundingClientRect().width ?? 0;
    event.preventDefault();

    const mousemove = (e: MouseEvent) => {
      requestAnimationFrame(() => {
        if (position === 'left') {
          const calW = startWidth - e.clientX;
          valueRef.current = wrapperWidth + calW;
        } else {
          const calW = e.clientX - startWidth;
          valueRef.current = wrapperWidth + calW;
        }
        if (valueRef.current > max) {
          document.body.style.cursor = 'w-resize';
        } else if (valueRef.current < min) {
          document.body.style.cursor = 'e-resize';
        } else {
          document.body.style.cursor = 'col-resize';
          if (target.current) {
            target.current.style.width = `${valueRef.current}px`;
          }
        }
      });
    };
    const mouseup = () => {
      requestAnimationFrame(() => {
        document.body.style.cursor = '';
        onDone?.(Math.max(Math.min(valueRef.current, max), min), 'width');
      });
      document.removeEventListener('mousemove', mousemove);
      document.removeEventListener('mouseup', mouseup);
    };
    document.addEventListener('mouseup', mouseup);
    document.addEventListener('mousemove', mousemove);
  });

  return (
    <div
      className={cx(
        'absolute w-1.5 h-full top-0 bottom-0 cursor-col-resize z-[2]',
        position === 'left' && '-left-1.5',
        position === 'right' && '-right-1.5',
      )}
      onMouseDown={onSplitMouseDown}
    />
  );
};

export const SplitLine = (props: SplitLineProps) => {
  const { type = 'horizontal', ...rest } = props;

  if (type === 'horizontal') {
    return <HorizontalSplitLine {...rest} />;
  }

  return <VerticalSplitLine {...rest} />;
};
